import { FullEngagementFragment, TriviaRoundStatus } from '@/generated/graphql';
import {
  TriviaAdminConfig,
  TriviaAdminData,
  TriviaViewConfig,
  TriviaViewData,
  useTriviaCloseAnswersMutation,
  useTriviaExtendTimerMutation,
  useTriviaNextQuestionMutation,
  useTriviaPreviousMutation,
  useTriviaResetMutation,
  useTriviaRevealMutation,
  useTriviaSkipRevealMutation,
  useTriviaStartRoundMutation,
} from '@/generated/graphql';
import {
  asTypedEngagement,
  TypedEngagement,
} from '@/utils/engagementTypeUtils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import useLocalStorage from 'use-local-storage';
import { useMemo, useState } from 'react';
import { LetterTile } from '../LetterTile';
import {
  TRIVIA_HIDE_ANSWERS_KEY,
  TRIVIA_MAX_TIMER_S,
  choiceColor,
  formatCountdown,
} from '../constants';
import { countdownRatio, useCountdown } from '../useCountdown';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import {
  isSkippedTriviaQuestion,
  triviaAdminPrimaryLabel,
  triviaAdminQuestionListMark,
  triviaAdminShowsSkipReveal,
} from '../adminSkip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PRIMARY =
  'bg-[#FF6B3D] text-black hover:bg-[#FF6B3D]/90 border-0 font-semibold';

function statusLabel(status: TriviaRoundStatus, skipped?: boolean) {
  if (skipped) return 'Skipped';
  switch (status) {
    case TriviaRoundStatus.QuestionOpen:
      return 'Answers open';
    case TriviaRoundStatus.QuestionClosed:
      return 'Answers locked';
    case TriviaRoundStatus.Revealed:
      return 'Revealed';
    case TriviaRoundStatus.RoundComplete:
      return 'Round complete';
    default:
      return 'Idle';
  }
}

function TriviaAdminControlViewInner({
  engagement,
}: {
  engagement: TypedEngagement<
    TriviaAdminData,
    TriviaAdminConfig,
    TriviaViewData,
    TriviaViewConfig
  >;
}) {
  const data = engagement.data;
  const config = engagement.config;
  const questions = config.questions ?? [];
  const status = data.status ?? TriviaRoundStatus.Idle;
  const index = data.questionIndex ?? 0;
  const question = questions[index];
  const last = index >= questions.length - 1;
  const skipRevealedQuestionIds = data.skipRevealedQuestionIds ?? [];
  const skippedCurrent = isSkippedTriviaQuestion(
    skipRevealedQuestionIds,
    question?.id,
  );
  const [hideAnswers, setHideAnswers] = useLocalStorage(
    TRIVIA_HIDE_ANSWERS_KEY,
    false,
  );
  const [resetOpen, setResetOpen] = useState(false);

  const [startRound, startState] = useTriviaStartRoundMutation();
  const [closeAnswers, closeState] = useTriviaCloseAnswersMutation();
  const [reveal, revealState] = useTriviaRevealMutation();
  const [nextQuestion, nextState] = useTriviaNextQuestionMutation();
  const [skipReveal, skipState] = useTriviaSkipRevealMutation();
  const [previous, prevState] = useTriviaPreviousMutation();
  const [reset, resetState] = useTriviaResetMutation();
  const [extendTimer, extendState] = useTriviaExtendTimerMutation();

  const mutating =
    startState.loading ||
    closeState.loading ||
    revealState.loading ||
    nextState.loading ||
    skipState.loading ||
    prevState.loading ||
    resetState.loading ||
    extendState.loading;

  const { remainingMs } = useCountdown(data.questionEndsAt);
  const timerMs = question?.timerMs ?? config.defaultTimerMs ?? 30_000;
  const remainingS = remainingMs == null ? null : remainingMs / 1000;
  const extendDisabled = remainingS != null && remainingS >= TRIVIA_MAX_TIMER_S;
  const totalAnswers = (data.tallies ?? []).reduce(
    (sum, t) => sum + t.count,
    0,
  );
  const playerCount = data.playerCount ?? 0;
  const answered = data.answeredCount ?? totalAnswers;
  const talliesById = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of data.tallies ?? []) map.set(t.choiceId, t.count);
    return map;
  }, [data.tallies]);

  const id = engagement.id!;
  const run = (fn: () => Promise<unknown>) => () => {
    void fn();
  };

  const hide = hideAnswers && status !== TriviaRoundStatus.RoundComplete;
  const showTallies =
    status === TriviaRoundStatus.QuestionOpen ||
    status === TriviaRoundStatus.QuestionClosed ||
    status === TriviaRoundStatus.Revealed;

  const primaryLabel = triviaAdminPrimaryLabel({
    status,
    skipped: skippedCurrent,
    last,
  });
  const primary = (() => {
    if (!primaryLabel) return null;
    if (primaryLabel === 'Start round') {
      return {
        label: primaryLabel,
        onClick: run(() => startRound({ variables: { engagementId: id } })),
      };
    }
    if (primaryLabel === 'Close answers') {
      return {
        label: primaryLabel,
        onClick: run(() => closeAnswers({ variables: { engagementId: id } })),
      };
    }
    if (primaryLabel === 'Reveal answer') {
      return {
        label: primaryLabel,
        onClick: run(() => reveal({ variables: { engagementId: id } })),
      };
    }
    return {
      label: primaryLabel,
      onClick: run(() => nextQuestion({ variables: { engagementId: id } })),
    };
  })();
  const showSkipReveal = triviaAdminShowsSkipReveal(status, skippedCurrent);

  const chipClass =
    status === TriviaRoundStatus.QuestionOpen
      ? 'border-green-500/45 bg-green-500/10 text-green-400'
      : status === TriviaRoundStatus.Idle
        ? 'border-white/20 bg-white/5 text-neutral-300'
        : 'border-[#FF6B3D]/45 bg-[#FF6B3D]/10 text-[#FF6B3D]';

  return (
    <div
      data-name="TRIVIA_ADMIN_CONTROL_VIEW"
      className="flex h-full w-full flex-col bg-background text-foreground"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <QuestionMarkCircledIcon className="h-6 w-6 text-[#FF6B3D]" />
            {engagement.title}
          </h2>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide',
              chipClass,
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                status === TriviaRoundStatus.QuestionOpen
                  ? 'bg-green-400 shadow-[0_0_8px_#4ade80]'
                  : 'bg-current',
              )}
            />
            {statusLabel(status, skippedCurrent)}
          </span>
          {questions.length > 0 ? (
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-neutral-300">
              Question {Math.min(index + 1, questions.length)} of{' '}
              {questions.length}
            </span>
          ) : null}
          {status !== TriviaRoundStatus.RoundComplete ? (
            <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
              Hide answers
              <Switch
                checked={!!hideAnswers}
                onCheckedChange={setHideAnswers}
              />
            </label>
          ) : null}
        </div>

        {hide ? (
          <div className="flex gap-2 rounded-lg border bg-white/5 px-2.5 py-2 text-xs leading-snug text-neutral-300">
            <strong>Answers hidden.</strong> The correct answer and tallies are
            masked here. Stage and phones aren&apos;t affected.
          </div>
        ) : null}

        <div className="grid min-h-0 flex-1 gap-6 md:grid-cols-[1fr_340px]">
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border bg-card p-5">
              {status === TriviaRoundStatus.Idle && question ? (
                <>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Up next · Question 1 of {questions.length}
                  </div>
                  <p className="mb-4 mt-1.5 text-[22px] font-bold leading-snug">
                    {question.text}
                  </p>
                  {question.choices.map((choice, i) => (
                    <TallyRow
                      key={choice.id}
                      index={i}
                      text={choice.text}
                      correct={choice.id === question.correctChoiceId}
                      hide={hide}
                      showCounts={false}
                    />
                  ))}
                  <div className="mt-3.5 flex gap-4 text-[13px] text-muted-foreground">
                    <span>
                      <b className="text-foreground">{playerCount}</b> players
                      joined
                    </span>
                    {config.useQuestionTimer ? (
                      <span>
                        Timer{' '}
                        <b className="text-foreground">
                          {Math.round((config.defaultTimerMs ?? 30000) / 1000)}s
                        </b>{' '}
                        per question
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}

              {(status === TriviaRoundStatus.QuestionOpen ||
                status === TriviaRoundStatus.QuestionClosed ||
                status === TriviaRoundStatus.Revealed) &&
              question ? (
                <>
                  <p className="mb-4 text-[22px] font-bold leading-snug">
                    {question.text}
                  </p>
                  {status === TriviaRoundStatus.QuestionOpen &&
                  data.questionEndsAt ? (
                    <div className="mb-4 flex items-center gap-3">
                      <span className="font-semibold tabular-nums">
                        {formatCountdown(remainingMs ?? 0)} left
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                        <i
                          className="block h-full rounded-full bg-[#FF6B3D]"
                          style={{
                            width: `${countdownRatio(remainingMs, timerMs) * 100}%`,
                          }}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={mutating || extendDisabled}
                        onClick={run(() =>
                          extendTimer({ variables: { engagementId: id } }),
                        )}
                      >
                        +15s
                      </Button>
                    </div>
                  ) : null}
                  {question.choices.map((choice, i) => {
                    const count = talliesById.get(choice.id) ?? 0;
                    const pct =
                      answered > 0 ? Math.round((count / answered) * 100) : 0;
                    return (
                      <TallyRow
                        key={choice.id}
                        index={i}
                        text={choice.text}
                        count={count}
                        pct={pct}
                        correct={choice.id === data.correctChoiceId}
                        hide={hide}
                        showCounts={showTallies}
                        dim={
                          status === TriviaRoundStatus.Revealed &&
                          choice.id !== data.correctChoiceId
                        }
                      />
                    );
                  })}
                  <div className="mt-3.5 text-[13px] text-muted-foreground">
                    {status === TriviaRoundStatus.QuestionOpen ? (
                      <span>
                        <b className="text-foreground">{answered}</b> of{' '}
                        {playerCount} answered · Live tallies are only on this
                        screen
                      </span>
                    ) : null}
                    {status === TriviaRoundStatus.QuestionClosed ? (
                      <span>
                        <b className="text-foreground">{answered}</b> of{' '}
                        {playerCount} answered ·{' '}
                        {Math.max(0, playerCount - answered)} didn&apos;t answer
                      </span>
                    ) : null}
                    {status === TriviaRoundStatus.Revealed ? (
                      <span>
                        {hide
                          ? 'Results hidden on this screen'
                          : `${data.correctCount ?? 0} of ${answered} got it right${
                              answered
                                ? ` (${Math.round(((data.correctCount ?? 0) / answered) * 100)}%)`
                                : ''
                            } · Scores updated`}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}

              {status === TriviaRoundStatus.RoundComplete ? (
                <Leaderboard
                  rows={data.leaderboard ?? []}
                  playerCount={playerCount}
                  questionCount={questions.length}
                />
              ) : null}
            </div>

            <div className="hidden items-center justify-between gap-2 md:flex">
              <div className="flex gap-2">
                {status === TriviaRoundStatus.QuestionClosed ||
                status === TriviaRoundStatus.Revealed ||
                skippedCurrent ? (
                  <Button
                    variant="ghost"
                    className="px-2 text-muted-foreground"
                    disabled={mutating || index === 0}
                    onClick={run(() =>
                      previous({ variables: { engagementId: id } }),
                    )}
                  >
                    ‹ Previous
                  </Button>
                ) : null}
                {status === TriviaRoundStatus.RoundComplete ? (
                  <Button
                    variant="ghost"
                    className="px-2 text-muted-foreground"
                    disabled={mutating}
                    onClick={run(() =>
                      previous({ variables: { engagementId: id } }),
                    )}
                  >
                    ‹ Back to question {questions.length}
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={mutating}
                  onClick={() => setResetOpen(true)}
                >
                  Reset round
                </Button>
                {showSkipReveal ? (
                  <Button
                    variant="outline"
                    disabled={mutating}
                    onClick={run(() =>
                      skipReveal({ variables: { engagementId: id } }),
                    )}
                  >
                    Skip reveal
                  </Button>
                ) : null}
                {primary ? (
                  <Button
                    className={PRIMARY}
                    disabled={mutating || questions.length === 0}
                    onClick={primary.onClick}
                  >
                    {primary.label}
                  </Button>
                ) : null}
              </div>
            </div>
            {status === TriviaRoundStatus.Idle ? (
              <p className="hidden text-right text-xs text-muted-foreground md:block">
                Start shows question 1 on the Stage and on phones.
              </p>
            ) : null}
          </div>

          {status !== TriviaRoundStatus.RoundComplete ? (
            <div className="hidden flex-col gap-4 md:flex">
              <div className="rounded-xl border bg-card p-4">
                <div className="mb-2 flex items-center justify-between text-[13px] font-bold">
                  Questions
                  <span className="font-medium text-muted-foreground">
                    Question {index + 1} of {questions.length}
                  </span>
                </div>
                {questions.map((q, i) => {
                  const skipped = isSkippedTriviaQuestion(
                    skipRevealedQuestionIds,
                    q.id,
                  );
                  const mark = triviaAdminQuestionListMark({
                    index: i,
                    currentIndex: index,
                    skipped,
                  });
                  return (
                    <div
                      key={q.id}
                      className={cn(
                        'flex items-center gap-2.5 border-b py-1.5 text-[13px] text-muted-foreground last:border-0',
                        i === index && 'font-semibold text-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-secondary text-[11px] font-bold',
                          mark === 'check' && 'bg-[#50DC78]/15 text-[#50DC78]',
                          mark === 'current' && 'bg-[#FF6B3D] text-black',
                        )}
                      >
                        {mark === 'check' ? '✓' : i + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{q.text}</span>
                      {skipped ? (
                        <span className="shrink-0 rounded-full border border-white/20 bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                          Skipped
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="rounded-xl border bg-card p-4">
                <div className="mb-2 flex items-center justify-between text-[13px] font-bold">
                  Scores so far
                  <span className="font-medium text-muted-foreground">
                    Top 5
                  </span>
                </div>
                {(data.leaderboard ?? []).length === 0 ||
                (data.revealedQuestionIds ?? []).length === 0 ? (
                  <p className="py-2 text-xs text-muted-foreground">
                    Scores show up after the first reveal.
                  </p>
                ) : (
                  (data.leaderboard ?? []).slice(0, 5).map((row) => (
                    <div
                      key={row.userId}
                      className="flex items-center gap-2.5 py-1.5 text-[13px]"
                    >
                      <span className="w-5 text-center font-bold text-muted-foreground">
                        {row.rank}
                      </span>
                      <span className="flex-1 truncate">{row.name}</span>
                      {row.tied ? (
                        <span className="text-[10px] font-bold text-[#F5B942]">
                          TIE
                        </span>
                      ) : null}
                      <span className="font-bold tabular-nums">
                        {row.score}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t bg-card p-3 md:hidden">
        {status !== TriviaRoundStatus.Idle ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" disabled={mutating}>
                ⋯
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {showSkipReveal ? (
                <DropdownMenuItem
                  onClick={run(() =>
                    skipReveal({ variables: { engagementId: id } }),
                  )}
                >
                  Skip reveal
                </DropdownMenuItem>
              ) : null}
              {(status === TriviaRoundStatus.QuestionClosed ||
                status === TriviaRoundStatus.Revealed ||
                status === TriviaRoundStatus.RoundComplete) &&
              (status === TriviaRoundStatus.RoundComplete || index > 0) ? (
                <DropdownMenuItem
                  onClick={run(() =>
                    previous({ variables: { engagementId: id } }),
                  )}
                >
                  {status === TriviaRoundStatus.RoundComplete
                    ? `Back to question ${questions.length}`
                    : 'Previous'}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                disabled={mutating}
                onClick={() => setResetOpen(true)}
              >
                Reset round…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {status === TriviaRoundStatus.Idle ? (
          <Button
            variant="outline"
            className="flex-1"
            disabled={mutating}
            onClick={() => setResetOpen(true)}
          >
            Reset round
          </Button>
        ) : null}
        {primary ? (
          <Button
            className={cn(PRIMARY, 'flex-1')}
            disabled={mutating || questions.length === 0}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="flex-1"
            disabled={mutating}
            onClick={() => setResetOpen(true)}
          >
            Reset round
          </Button>
        )}
      </div>

      <ConfirmActionDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Reset this round?"
        description="This clears every answer and score and goes back to question 1. Players stay joined. You can't undo this."
        confirmLabel="Reset round"
        loading={resetState.loading}
        onConfirm={() => reset({ variables: { engagementId: id } })}
      />
    </div>
  );
}

function TallyRow({
  index,
  text,
  count,
  pct,
  correct,
  hide,
  showCounts,
  dim,
}: {
  index: number;
  text: string;
  count?: number;
  pct?: number;
  correct?: boolean;
  hide?: boolean;
  showCounts?: boolean;
  dim?: boolean;
}) {
  return (
    <div
      className={cn(
        'relative mt-2 flex items-center gap-3 overflow-hidden rounded-lg border bg-[#0e0806] px-3 py-2.5',
        correct && !hide && 'border-[#50DC78]/60',
        dim && 'opacity-55',
      )}
    >
      {showCounts && !hide ? (
        <span
          className="absolute bottom-0 left-0 top-0 opacity-20"
          style={{
            width: `${pct ?? 0}%`,
            background: choiceColor(index),
          }}
        />
      ) : null}
      <LetterTile index={index} />
      <span className="relative min-w-0 flex-1 text-[15px] font-medium">
        {text}
      </span>
      {correct && !hide ? (
        <span className="relative inline-flex items-center gap-1 rounded-md border border-[#50DC78]/40 bg-[#50DC78]/10 px-1.5 py-0.5 text-[11px] font-bold text-[#50DC78]">
          ✓ Correct
        </span>
      ) : null}
      {showCounts ? (
        hide ? (
          <span className="relative grid h-[22px] min-w-[74px] place-items-center rounded-md border text-[11px] tracking-[0.2em] text-neutral-500 [background:repeating-linear-gradient(135deg,#1c1c1c_0_6px,#262626_6px_12px)]">
            •••
          </span>
        ) : (
          <>
            <span className="relative min-w-[34px] text-right text-[15px] font-bold tabular-nums">
              {count ?? 0}
            </span>
            <span className="relative min-w-[40px] text-right text-[13px] tabular-nums text-muted-foreground">
              {pct ?? 0}%
            </span>
          </>
        )
      ) : null}
    </div>
  );
}

function Leaderboard({
  rows,
  playerCount,
  questionCount,
}: {
  rows: TriviaAdminData['leaderboard'];
  playerCount: number;
  questionCount: number;
}) {
  const avg =
    rows && rows.length
      ? (rows.reduce((s, r) => s + r.score, 0) / rows.length).toFixed(1)
      : '0';
  return (
    <div>
      <div className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Final scores
      </div>
      <p className="mb-3 text-[22px] font-bold leading-snug">
        Thanks for playing!
      </p>
      {(rows ?? []).map((row) => (
        <div
          key={row.userId}
          className="flex items-center gap-2.5 border-b py-1.5 text-sm last:border-0"
        >
          <span className="w-7 text-center font-bold text-muted-foreground">
            {row.rank}
          </span>
          <span className="flex-1 truncate">{row.name}</span>
          {row.tied ? (
            <span className="text-[10px] font-bold text-[#F5B942]">TIE</span>
          ) : null}
          <span className="font-bold tabular-nums">
            {row.score} / {questionCount}
          </span>
        </div>
      ))}
      <div className="mt-3.5 text-[13px] text-muted-foreground">
        <b className="text-foreground">{playerCount}</b> players · Average score{' '}
        <b className="text-foreground">
          {avg} / {questionCount}
        </b>
      </div>
    </div>
  );
}

export function TriviaAdminControlView({
  engagement,
}: {
  engagement: FullEngagementFragment;
}) {
  const typed = asTypedEngagement<
    TriviaAdminData,
    TriviaAdminConfig,
    TriviaViewData,
    TriviaViewConfig
  >(engagement);
  return <TriviaAdminControlViewInner engagement={typed} />;
}
